import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { UserCheck, Upload, FileText, Building, User, Check } from 'lucide-react';
import ComplianceFooter from './ComplianceFooter';

interface ComplianceStepProps {
  onComplianceVerified: () => void;
  complianceVerified: boolean;
}

const ComplianceStep: React.FC<ComplianceStepProps> = ({ onComplianceVerified, complianceVerified }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company: '',
    role: '',
    isInstitutionalInvestor: false,
    isAccreditedInvestor: false,
    amlCheck: false,
    pepCheck: false
  });
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [identityVerificationCompleted, setIdentityVerificationCompleted] = useState(false);
  const [additionalConfirmations, setAdditionalConfirmations] = useState({
    noCriminalRecord: false,
    noFraudHistory: false,
    noLitigation: false,
    noBankruptcy: false,
    uboVerified: false,
    validRegistration: false,
    validLicenses: false,
    auditedStatements: false,
    noSanctions: false,
    strongCredit: false,
    goodGovernance: false,
    taxCompliant: false,
    pepDisclosed: false,
    authorizedSignatories: false,
    cleanBackground: false,
    credibleReferences: false
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 2,
    onDrop: (acceptedFiles) => {
      setUploadedFiles(prev => [...prev, ...acceptedFiles]);
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckboxChange = (field: keyof typeof formData) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleAdditionalConfirmationChange = (field: keyof typeof additionalConfirmations) => {
    setAdditionalConfirmations(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSelectAllConfirmations = () => {
    const allChecked = Object.values(additionalConfirmations).every(value => value) && formData.amlCheck && formData.pepCheck;
    
    setAdditionalConfirmations({
      noCriminalRecord: !allChecked,
      noFraudHistory: !allChecked,
      noLitigation: !allChecked,
      noBankruptcy: !allChecked,
      uboVerified: !allChecked,
      validRegistration: !allChecked,
      validLicenses: !allChecked,
      auditedStatements: !allChecked,
      noSanctions: !allChecked,
      strongCredit: !allChecked,
      goodGovernance: !allChecked,
      taxCompliant: !allChecked,
      pepDisclosed: !allChecked,
      authorizedSignatories: !allChecked,
      cleanBackground: !allChecked,
      credibleReferences: !allChecked
    });
    
    setFormData(prev => ({
      ...prev,
      amlCheck: !allChecked,
      pepCheck: !allChecked
    }));
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if either files are uploaded OR identity verification is marked as completed later
    const identityVerificationValid = uploadedFiles.length > 0 || identityVerificationCompleted;
    
    if (!formData.company || !formData.role || !identityVerificationValid) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2500));
      onComplianceVerified();
      navigate('/data-room');
    } catch (error) {
      console.error('Error verifying compliance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (complianceVerified) {
    navigate('/data-room');
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-2xl mx-auto"
    >
      <div className="card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <UserCheck className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Compliance Verification
          </h2>
          <p className="text-neutral-600">
            To comply with international due diligence standards, we require a brief identity confirmation. All data is encrypted and securely stored.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ID Upload Section */}
          <div className="border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-primary-600" />
              Identity Verification
            </h3>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive 
                  ? 'border-primary-500 bg-primary-50' 
                  : 'border-neutral-300 hover:border-primary-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
              {isDragActive ? (
                <p className="text-primary-600 font-medium">Drop the files here...</p>
              ) : (
                <div>
                  <p className="text-neutral-600 font-medium mb-2">
                    Upload ID or Passport
                  </p>
                  <p className="text-sm text-neutral-500">
                    Drag & drop files here, or click to select files
                  </p>
                  <p className="text-xs text-neutral-400 mt-2">
                    Accepted: JPG, PNG, PDF (max 2 files)
                  </p>
                </div>
              )}
            </div>

            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-neutral-700">Uploaded Files:</p>
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-4 h-4 text-neutral-500" />
                      <span className="text-sm text-neutral-700">{file.name}</span>
                      <span className="text-xs text-neutral-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Identity Verification Completion Checkmark */}
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={identityVerificationCompleted}
                  onChange={() => setIdentityVerificationCompleted(!identityVerificationCompleted)}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">
                    I will complete identity verification after reviewing the documents
                  </span>
                  <p className="text-xs text-neutral-500 mt-1">
                    You can upload your ID or passport later when you're ready
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Business Information */}
          <div className="border border-neutral-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <Building className="w-5 h-5 mr-2 text-primary-600" />
              Business Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-neutral-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-neutral-700 mb-2">
                  Your Role
                </label>
                <input
                  type="text"
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="e.g., CEO, Investor, Director"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Investor Type
                </label>
                <div className="flex flex-col space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="isInstitutionalInvestor"
                      checked={formData.isInstitutionalInvestor}
                      onChange={() => handleCheckboxChange('isInstitutionalInvestor')}
                      className="w-5 h-5 accent-[#cba864]"
                    />
                    <span className="text-sm text-neutral-700">I am an institutional investor</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="isAccreditedInvestor"
                      checked={formData.isAccreditedInvestor}
                      onChange={() => handleCheckboxChange('isAccreditedInvestor')}
                      className="w-5 h-5 accent-[#cba864]"
                    />
                    <span className="text-sm text-neutral-700">I am an accredited investor</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Optional Checks */}
          <div className="border border-neutral-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                <User className="w-5 h-5 mr-2" color="#cba864" />
                Additional Verification
              </h3>
              <button
                type="button"
                onClick={handleSelectAllConfirmations}
                className="text-sm px-3 py-1 bg-[#cba864] text-white rounded hover:bg-[#b89a5a] transition-colors"
              >
                {Object.values(additionalConfirmations).every(value => value) && formData.amlCheck && formData.pepCheck ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="amlCheck"
                  checked={formData.amlCheck}
                  onChange={() => handleCheckboxChange('amlCheck')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">
                    Anti-Money Laundering (AML) Check
                  </span>
                  <p className="text-xs text-neutral-500 mt-1">
                    Confirm compliance with AML regulations
                  </p>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="pepCheck"
                  checked={formData.pepCheck}
                  onChange={() => handleCheckboxChange('pepCheck')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">
                    Politically Exposed Person (PEP) Check
                  </span>
                  <p className="text-xs text-neutral-500 mt-1">
                    Verify if you are a politically exposed person
                  </p>
                </div>
              </label>

              {/* Additional Confirmations */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.noCriminalRecord}
                  onChange={() => handleAdditionalConfirmationChange('noCriminalRecord')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we have no criminal record.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.noFraudHistory}
                  onChange={() => handleAdditionalConfirmationChange('noFraudHistory')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we have no history of fraud, corruption, embezzlement, or financial misconduct.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.noLitigation}
                  onChange={() => handleAdditionalConfirmationChange('noLitigation')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we are not involved in any ongoing or past litigation that poses financial or reputational risk.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.noBankruptcy}
                  onChange={() => handleAdditionalConfirmationChange('noBankruptcy')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we have not declared bankruptcy, nor are we undergoing insolvency or restructuring proceedings.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.uboVerified}
                  onChange={() => handleAdditionalConfirmationChange('uboVerified')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that our Ultimate Beneficial Ownership (UBO) has been clearly identified and verified.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.validRegistration}
                  onChange={() => handleAdditionalConfirmationChange('validRegistration')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we hold valid and current company registration and incorporation documents.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.validLicenses}
                  onChange={() => handleAdditionalConfirmationChange('validLicenses')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that all required operating licenses and permits are in place and valid.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.auditedStatements}
                  onChange={() => handleAdditionalConfirmationChange('auditedStatements')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that our financial statements have been audited by a reputable, independent audit firm.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.noSanctions}
                  onChange={() => handleAdditionalConfirmationChange('noSanctions')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we do not appear on any international sanctions or watchlists (e.g., OFAC, EU, UN).</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.strongCredit}
                  onChange={() => handleAdditionalConfirmationChange('strongCredit')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we maintain a strong credit profile with manageable debt exposure.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.goodGovernance}
                  onChange={() => handleAdditionalConfirmationChange('goodGovernance')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we practice transparent and responsible corporate governance.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.taxCompliant}
                  onChange={() => handleAdditionalConfirmationChange('taxCompliant')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we are fully compliant with all relevant tax laws and obligations.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.pepDisclosed}
                  onChange={() => handleAdditionalConfirmationChange('pepDisclosed')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that any Politically Exposed Persons (PEPs) associated with our entity have been properly disclosed and screened.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.authorizedSignatories}
                  onChange={() => handleAdditionalConfirmationChange('authorizedSignatories')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that all signatories to this agreement are legally authorized to bind the company.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.cleanBackground}
                  onChange={() => handleAdditionalConfirmationChange('cleanBackground')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that background checks on our executives and directors will show no adverse findings.</span>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={additionalConfirmations.credibleReferences}
                  onChange={() => handleAdditionalConfirmationChange('credibleReferences')}
                  className="w-5 h-5 accent-[#cba864] flex-shrink-0 mt-0.5" 
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-neutral-700">We confirm that we are able to provide credible references and a verifiable history of successful business transactions.</span>
                </div>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={!formData.company || !formData.role || (uploadedFiles.length === 0 && !identityVerificationCompleted) || isLoading}
            className="btn-primary w-full flex items-center justify-center"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Verifying Compliance...
              </div>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Complete Verification & Continue
              </>
            )}
          </button>
        </form>

        <ComplianceFooter />
      </div>
    </motion.div>
  );
};

export default ComplianceStep; 